import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import api from "lib/api/api";
import {
  applyCouponAction,
  createBankTransferPaymentAction,
  createPaymentCheckoutAction,
  instaPayEnrollAction,
  uploadBankTransferReceiptAction,
} from "../../../redux/Actions/instaPayEnrollAction";
import { setError } from "./../../../redux/Actions/errorActions";

// Stable fallback to avoid creating a new object per select
const EMPTY_STATUS = {};

const COUPON_MESSAGE_KEYS = {
  "you have already used this coupon before.":
    "joinNowContent.payment.couponAlreadyUsed",
};

const PAYMENT_ERROR_MESSAGE_KEYS = [
  {
    includes: "exceeded your 5 request attempts",
    key: "joinNowContent.payment.errors.requestLimitExceeded",
  },
  {
    includes: "already enrolled",
    key: "joinNowContent.payment.errors.alreadyEnrolled",
  },
  {
    includes: "paypal does not support currency",
    key: "joinNowContent.payment.errors.paypalCurrencyUnsupported",
  },
  {
    includes: "accept the course terms",
    key: "joinNowContent.payment.errors.termsRequired",
  },
];

const AR_CURRENCY_LABEL_KEYS = {
  EGP: "currency.egp",
  EUR: "currency.eur",
  AED: "currency.aed",
};

const normalizeCurrencyCode = (currencyValue) => {
  if (currencyValue === null || currencyValue === undefined) {
    return "";
  }
  return String(currencyValue).trim().toUpperCase();
};

const PaymentOptions = ({
  teacherCourseId,
  parentCourseId,
  level,
  onClose,
  priceCurrency,
  termsAcceptance,
  termsAcceptanceComplete = false,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  // ?? local input for coupon
  const [couponCode, setCouponCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("STRIPE");
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [paymentQuoteLoading, setPaymentQuoteLoading] = useState(false);
  const [paymentAvailability, setPaymentAvailability] = useState({
    generalPaymentsEnabled: true,
    onlinePaymentsEnabled: true,
    egyptianBankTransferAllowed: false,
    instaPayAllowed: false,
  });
  const bankTransferResultRef = useRef(null);
  const qrCodeRef = useRef(null);

  const isArabic = i18n.language?.startsWith("ar");
  const getCurrencyLabel = (currencyValue) => {
    const normalized = normalizeCurrencyCode(currencyValue);
    if (!normalized) {
      return "-";
    }
    if (!isArabic) {
      return normalized;
    }
    const currencyKey = AR_CURRENCY_LABEL_KEYS[normalized];
    return currencyKey ? t(currencyKey) : currencyValue;
  };

  const statusKey = teacherCourseId || parentCourseId;

  // existing payment state
  const { loading, successMessage, error, coupon, checkout, bankTransfer, receiptUploading, receiptError } =
    useSelector((state) => state.instaPayStatus?.[statusKey] ?? EMPTY_STATUS);

  // derived coupon states from redux
  const couponData = coupon?.data;
  const couponError = coupon?.error;
  const couponLoading = coupon?.loading;
  const normalizedCouponMessage = couponData?.message
    ?.trim()
    ?.toLowerCase();
  const couponMessageKey =
    normalizedCouponMessage &&
    COUPON_MESSAGE_KEYS[normalizedCouponMessage];
  const couponMessage = couponMessageKey
    ? t(couponMessageKey)
    : couponData?.message;
  const couponCurrency = getCurrencyLabel(
    couponData?.currency || priceCurrency || "�"
  );
  const shouldHidePriceDetails =
    couponMessageKey === "joinNowContent.payment.couponAlreadyUsed";
  const showCouponPriceDetails =
    !shouldHidePriceDetails &&
    couponData?.originalPrice !== undefined &&
    couponData?.originalPrice !== null &&
    couponData?.discountedPrice !== undefined &&
    couponData?.discountedPrice !== null;

  const onlinePaymentOptions = useMemo(() => [
    {
      id: "STRIPE",
      label: t("joinNowContent.payment.methodStripe"),
      logo: "/stripe-logo.svg",
      helperText: t("joinNowContent.payment.onlineProviderHint"),
    },
    {
      id: "PAYPAL",
      label: t("joinNowContent.payment.methodPayPal"),
      logo: "/paypal-logo.svg",
      helperText: t("joinNowContent.payment.onlineProviderHint"),
    },
  ], [t]);

  const manualPaymentOptions = useMemo(() => [
    {
      id: "BANK_TRANSFER_SEPA",
      label: "SEPA bank transfer",
      logo: "/banktransfer.jpg",
      helperText: "EUR transfer with GiroCode / EPC QR",
      transferType: "SEPA_EUR",
    },
    {
      id: "BANK_TRANSFER_SWIFT",
      label: "SWIFT global transfer",
      logo: "/banktransfer.jpg",
      helperText: "International EUR bank transfer instructions",
      transferType: "SWIFT_GLOBAL",
    },
    {
      id: "banqueMasr",
      label: t("joinNowContent.payment.methodBanqueMasr"),
      logo: "/banktransfer.jpg",
      helperText: t("joinNowContent.payment.manualProviderHint"),
    },
    {
      id: "instaPay",
      label: t("joinNowContent.payment.methodInstaPay"),
      logo: "/instapayLogo.png",
      helperText: t("joinNowContent.payment.manualProviderHint"),
    },
  ], [t]);
  const visibleOnlinePaymentOptions = useMemo(
    () => paymentAvailability.onlinePaymentsEnabled ? onlinePaymentOptions : [],
    [onlinePaymentOptions, paymentAvailability.onlinePaymentsEnabled]
  );
  const visibleManualPaymentOptions = useMemo(() => manualPaymentOptions.filter((option) => {
    if (option.id === "banqueMasr") return paymentAvailability.egyptianBankTransferAllowed;
    if (option.id === "instaPay") return paymentAvailability.instaPayAllowed;
    return paymentAvailability.generalPaymentsEnabled;
  }), [
    manualPaymentOptions,
    paymentAvailability.egyptianBankTransferAllowed,
    paymentAvailability.instaPayAllowed,
    paymentAvailability.generalPaymentsEnabled,
  ]);
  const isOnlinePayment = onlinePaymentOptions.some(
    (option) => option.id === paymentMethod
  );
  const selectedManualOption = visibleManualPaymentOptions.find((option) => option.id === paymentMethod);
  const isBankTransfer = paymentMethod.startsWith("BANK_TRANSFER_");
  const quoteProvider = isOnlinePayment ? paymentMethod : "BANK_TRANSFER";
  const getPaymentErrorMessage = (message) => {
    if (!message) {
      return null;
    }
    const normalizedMessage = String(message).trim().toLowerCase();
    const match = PAYMENT_ERROR_MESSAGE_KEYS.find(({ includes }) =>
      normalizedMessage.includes(includes)
    );
    return match ? t(match.key) : message;
  };

  useEffect(() => {
    return () => {
      if (teacherCourseId) {
        dispatch({ type: "CLEAR_STATUS_ERROR", teacherCourseId });
      }
      if (parentCourseId) {
        dispatch({ type: "CLEAR_STATUS_ERROR", parentCourseId });
      }
    };
  }, [dispatch, parentCourseId, teacherCourseId]);

  useEffect(() => {
    let ignore = false;

    const fetchPaymentMethodAvailability = async () => {
      try {
        const response = await api.get("/payments/method-availability");
        if (!ignore) {
          setPaymentAvailability({
            generalPaymentsEnabled: response.data?.generalPaymentsEnabled !== false,
            onlinePaymentsEnabled: response.data?.onlinePaymentsEnabled !== false,
            egyptianBankTransferAllowed: Boolean(response.data?.egyptianBankTransferAllowed),
            instaPayAllowed: Boolean(response.data?.instaPayAllowed),
          });
        }
      } catch (error) {
        if (!ignore) {
          setPaymentAvailability((current) => ({ ...current, egyptianBankTransferAllowed: false, instaPayAllowed: false }));
        }
      }
    };

    fetchPaymentMethodAvailability();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const visibleOptions = [...visibleOnlinePaymentOptions, ...visibleManualPaymentOptions];
    if (!visibleOptions.some((option) => option.id === paymentMethod)) {
      setPaymentMethod(visibleOptions[0]?.id || "");
    }
  }, [paymentMethod, visibleManualPaymentOptions, visibleOnlinePaymentOptions]);

  useEffect(() => {
    if (!teacherCourseId || !paymentMethod) {
      setPaymentQuote(null);
      return undefined;
    }
    let ignore = false;
    const fetchPaymentQuote = async () => {
      setPaymentQuoteLoading(true);
      try {
        const response = await api.get("/payments/quote", {
          params: {
            teacherCourseId,
            provider: quoteProvider,
            ...(couponData?.code ? { couponCode: couponData.code } : {}),
          },
        });
        if (!ignore) setPaymentQuote(response.data);
      } catch (_error) {
        if (!ignore) setPaymentQuote(null);
      } finally {
        if (!ignore) setPaymentQuoteLoading(false);
      }
    };
    fetchPaymentQuote();
    return () => {
      ignore = true;
    };
  }, [couponData?.code, paymentMethod, quoteProvider, teacherCourseId]);

  useEffect(() => {
    if (successMessage && !checkout && !bankTransfer) {
      dispatch(setError(t("joinNowContent.payment.success"), "success"));
      onClose();
    }
  }, [bankTransfer, checkout, dispatch, onClose, successMessage, t]);

  useEffect(() => {
    if (!bankTransfer) {
      return;
    }

    const target = bankTransfer.qrCodeBase64
      ? qrCodeRef.current
      : bankTransferResultRef.current;

    window.setTimeout(() => {
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [bankTransfer]);

  // ?? Apply coupon
  const handleApplyCoupon = () => {
    if (!couponCode.trim()) return;
    dispatch(applyCouponAction(teacherCourseId, couponCode));
  };

  // ?? Confirm booking
  const handlePaymentConfirm = () => {
    if (!termsAcceptanceComplete) {
      return;
    }
    const codeToSend = couponData?.code || couponCode?.trim() || null;

    if (isOnlinePayment) {
      dispatch(createPaymentCheckoutAction(teacherCourseId, paymentMethod, codeToSend, termsAcceptance));
    } else if (isBankTransfer) {
      dispatch(createBankTransferPaymentAction(teacherCourseId, selectedManualOption.transferType, codeToSend, termsAcceptance));
    } else if (selectedManualOption) {
      dispatch(instaPayEnrollAction(teacherCourseId, codeToSend, termsAcceptance, paymentMethod));
    }

  };

  const handleForceNewCheckout = () => {
    if (!termsAcceptanceComplete || !checkout?.existingPending) {
      return;
    }
    const codeToSend = couponData?.code || couponCode?.trim() || null;
    dispatch(createPaymentCheckoutAction(teacherCourseId, paymentMethod, codeToSend, termsAcceptance, true));
  };

  const paymentStatusUrl = checkout?.transactionId
    ? `/payment/success?transactionId=${encodeURIComponent(checkout.transactionId)}&provider=${encodeURIComponent(
        checkout.provider || paymentMethod
      )}`
    : null;

  const handleCopy = (value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
  };

  const handleReceiptUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !bankTransfer?.transactionId) return;
    dispatch(uploadBankTransferReceiptAction(teacherCourseId, bankTransfer.transactionId, file));
    event.target.value = "";
  };

  const renderPaymentOption = (option) => {
    const isSelected = paymentMethod === option.id;
    return (
      <Paper
        key={option.id}
        elevation={0}
        role="button"
        tabIndex={0}
        onClick={() => setPaymentMethod(option.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setPaymentMethod(option.id);
          }
        }}
        sx={{
          bgcolor: isSelected ? "#fffbe6" : "#f8fafa",
          border: isSelected ? "2px solid #FFD600" : "1px solid #e0e0e0",
          borderRadius: 2,
          px: 2,
          py: 1.5,
          minWidth: 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          cursor: "pointer",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            component="img"
            src={option.logo}
            alt={option.label}
            sx={{ width: 40, height: 30, objectFit: "contain" }}
          />
          <Box sx={{ textAlign: isArabic ? "right" : "left" }}>
            <Typography variant="body1" fontWeight={700}>
              {option.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.helperText}
            </Typography>
          </Box>
        </Box>
        {isSelected && <CheckCircleIcon sx={{ color: "#2e7d32" }} />}
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        p: 3,
      }}
    >
      {/* Title */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {t("joinNowContent.payment.title", { level })}
      </Typography>

      {/* Payment Method Box */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {visibleOnlinePaymentOptions.length > 0 && <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CreditCardIcon fontSize="small" />
            <Typography fontWeight={800}>
              {t("joinNowContent.payment.onlineTitle")}
            </Typography>
            <Chip
              size="small"
              label={t("joinNowContent.payment.instantLabel")}
            />
          </Box>}
          {visibleOnlinePaymentOptions.map(renderPaymentOption)}

          {visibleManualPaymentOptions.length > 0 && <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <ReceiptLongIcon fontSize="small" />
            <Typography fontWeight={800}>
              {t("joinNowContent.payment.manualTitle")}
            </Typography>
          </Box>}
          {visibleManualPaymentOptions.map(renderPaymentOption)}
          {visibleOnlinePaymentOptions.length === 0 && visibleManualPaymentOptions.length === 0 && (
            <Alert severity="info">Payment methods are currently unavailable.</Alert>
          )}
        </Box>
      </Paper>
      {/* ?? Coupon Section */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, justifyContent: "center" }}>
        <TextField
          size="small"
          label={t("joinNowContent.payment.couponLabel")}
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          sx={{ width: "60%" }}
        />
        <Button
          onClick={handleApplyCoupon}
          disabled={couponLoading}
          variant="outlined"
          sx={{
            borderColor: "#FFD600",
            color: "#111",
            fontWeight: "bold",
            fontSize: "0.875rem",
            py: 1,
            px: 3,
            "&:hover": {
              borderColor: "#e6ac00",
              backgroundColor: "#fffbe6",
            },
          }}
        >
          {couponLoading
            ? t("joinNowContent.payment.loadingCoupon")
            : t("joinNowContent.payment.applyCoupon")}
        </Button>
      </Box>
      {/* Coupon Feedback */}
      {couponError && (
        <Typography color="error" mb={1}>
          {couponError}
        </Typography>
      )}
      {couponData && (
        <Box sx={{ mb: 2 }}>
          {showCouponPriceDetails && (
            <>
              <Typography variant="body1">
                {t("joinNowContent.payment.originalPrice")}:{" "}
                <strong>
                  {couponCurrency} {couponData.originalPrice}
                </strong>
              </Typography>
              <Typography variant="body1" color="success.main">
                {t("joinNowContent.payment.discountedPrice")}:{" "}
                <strong>
                  {couponCurrency} {couponData.discountedPrice}
                </strong>
              </Typography>
            </>
          )}

          {couponMessage && (
            <Typography
              variant="body2"
              color={
                couponMessageKey ===
                "joinNowContent.payment.couponAlreadyUsed"
                  ? "warning.main"
                  : "text.secondary"
              }
            >
              {couponMessage}
            </Typography>
          )}
        </Box>
      )}
      {paymentQuoteLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("joinNowContent.payment.loadingQuote")}
        </Typography>
      )}
      {paymentQuote && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: isArabic ? "right" : "left" }}>
          <Typography>{t("joinNowContent.payment.coursePrice")}: <strong>{getCurrencyLabel(paymentQuote.currency)} {paymentQuote.coursePrice}</strong></Typography>
          {paymentQuote.discountAmount > 0 && (
            <Typography color="success.main">{t("joinNowContent.payment.discountAmount")}: <strong>-{getCurrencyLabel(paymentQuote.currency)} {paymentQuote.discountAmount}</strong></Typography>
          )}
          <Typography>{t("joinNowContent.payment.paymentFee")}: <strong>{getCurrencyLabel(paymentQuote.currency)} {paymentQuote.paymentFee}</strong></Typography>
          <Divider sx={{ my: 1 }} />
          <Typography fontWeight={900}>{t("joinNowContent.payment.totalAmount")}: {getCurrencyLabel(paymentQuote.currency)} {paymentQuote.totalAmount}</Typography>
        </Paper>
      )}
      <Alert severity={isOnlinePayment ? "info" : "success"} sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}>
        {isOnlinePayment
          ? t("joinNowContent.payment.onlineFeeNotice", {
              fee: paymentQuote?.paymentFee,
              currency: getCurrencyLabel(paymentQuote?.currency),
            })
          : t("joinNowContent.payment.noBankFeeNotice")}
      </Alert>
      {checkout?.existingPending && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            textAlign: isArabic ? "right" : "left",
            alignItems: "flex-start",
          }}
        >
          <Typography fontWeight={900} sx={{ mb: 0.75 }}>
            {t("joinNowContent.payment.pendingTransaction.title")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t("joinNowContent.payment.pendingTransaction.body")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {paymentStatusUrl && (
              <Button
                size="small"
                variant="contained"
                href={paymentStatusUrl}
                sx={{
                  bgcolor: "#FFC107",
                  color: "#111",
                  fontWeight: 900,
                  "&:hover": { bgcolor: "#E6AC00" },
                }}
              >
                {t("joinNowContent.payment.pendingTransaction.checkStatus")}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={handleForceNewCheckout}
              disabled={loading}
              sx={{ fontWeight: 800 }}
            >
              {t("joinNowContent.payment.pendingTransaction.startNew")}
            </Button>
          </Box>
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && !bankTransfer && !checkout?.existingPending && (
        <Box mb={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            <CheckCircleIcon sx={{ color: "green" }} />
            <Typography sx={{ color: "green" }}>
              {checkout
                ? t("joinNowContent.payment.onlineCheckoutOpened")
                : t("joinNowContent.payment.success")}
            </Typography>
          </Box>
          {checkout?.checkoutUrl && (
            <Box mt={1.5}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t("joinNowContent.payment.checkoutFallbackHint")}
              </Typography>
              <Button
                href={checkout.checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#FFD600",
                  color: "#111",
                  fontWeight: "bold",
                  "&:hover": {
                    borderColor: "#e6ac00",
                    backgroundColor: "#fffbe6",
                  },
                }}
              >
                {t("joinNowContent.payment.openCheckout")}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {bankTransfer && (
        <Paper
          ref={bankTransferResultRef}
          elevation={0}
          sx={{
            textAlign: isArabic ? "right" : "left",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            mb: 2,
            bgcolor: "#fbfcfc",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography fontWeight={900}>Bank transfer created</Typography>
            <AccountBalanceIcon fontSize="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Instructions were also sent to your email. Use the exact payment reference.
          </Typography>
          <Box
            sx={{
              border: "1px solid #e8ecef",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {[
              ["Account holder", bankTransfer.accountHolder],
              ["Bank", bankTransfer.bankName],
              ["IBAN", bankTransfer.iban],
              ["BIC / SWIFT", bankTransfer.bic],
              ["Amount", `${bankTransfer.currency} ${Number(bankTransfer.amount || 0).toFixed(2)}`],
              ["Verwendungszweck", bankTransfer.paymentReference],
            ].map(([label, value], index) => (
              <Box
                key={label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "160px minmax(0, 1fr) 92px" },
                  gap: { xs: 0.75, sm: 1.5 },
                  alignItems: "center",
                  px: 1.5,
                  py: 1.1,
                  borderTop: index === 0 ? 0 : "1px solid #eef1f3",
                  direction: "ltr",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                  color="text.secondary"
                  sx={{ textAlign: isArabic ? "right" : "left" }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#111",
                    fontWeight: label === "Verwendungszweck" ? 800 : 500,
                    overflowWrap: "anywhere",
                    textAlign: "left",
                  }}
                >
                  {value || "-"}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => handleCopy(value)}
                  sx={{
                    justifySelf: { xs: "start", sm: "end" },
                    minWidth: 84,
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  Copy
                </Button>
              </Box>
            ))}
          </Box>
          {bankTransfer.bankAddress && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Bank address:</strong> {bankTransfer.bankAddress}
            </Typography>
          )}
          {bankTransfer.qrCodeBase64 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box ref={qrCodeRef} sx={{ textAlign: "center", scrollMarginTop: 24 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#fff",
                    border: "1px solid #e2e7ea",
                    borderRadius: 2,
                    p: 1.5,
                    maxWidth: "100%",
                  }}
                >
                  <Box
                    component="img"
                    src={`data:image/png;base64,${bankTransfer.qrCodeBase64}`}
                    alt="SEPA GiroCode QR"
                    sx={{
                      display: "block",
                      width: { xs: 240, sm: 300 },
                      height: { xs: 240, sm: 300 },
                      maxWidth: "calc(100vw - 96px)",
                      objectFit: "contain",
                      imageRendering: "pixelated",
                    }}
                  />
                </Box>
                <Typography variant="caption" display="block" color="text.secondary">
                  Scan with your German/EU banking app.
                </Typography>
              </Box>
            </>
          )}
          <Divider sx={{ my: 2 }} />
          <Button variant="outlined" component="label" disabled={receiptUploading} sx={{ textTransform: "none" }}>
            {receiptUploading ? "Uploading receipt..." : "Upload transfer receipt"}
            <input hidden type="file" accept="image/*,application/pdf" onChange={handleReceiptUpload} />
          </Button>
          {receiptError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {receiptError}
            </Typography>
          )}
          {bankTransfer.receiptOriginalFilename && (
            <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
              Receipt uploaded: {bankTransfer.receiptOriginalFilename}
            </Typography>
          )}
        </Paper>
      )}
      {error && <Typography color="error">{getPaymentErrorMessage(error)}</Typography>}
      {/* Confirm Button */}
      <Button
        fullWidth
        disabled={loading || successMessage || !paymentMethod || !termsAcceptanceComplete}
        onClick={handlePaymentConfirm}
        sx={{
          backgroundColor: "#FFC107",
          color: "black",
          fontWeight: "bold",
          borderRadius: "50px",
          py: 1.5,
          mt: 2,
          "&:hover": { backgroundColor: "#e6ac00" },
        }}
      >
        {loading && isOnlinePayment
          ? t("joinNowContent.payment.redirecting")
          : isOnlinePayment
          ? t("joinNowContent.payment.payOnline")
          : isBankTransfer
          ? "Create bank transfer instructions"
          : t("joinNowContent.payment.confirm")}
      </Button>
    </Box>
  );
};

export default PaymentOptions;
